import { dbService as db } from "../firebaseInstance";

/**
 * 문서 크기 계산 유틸리티
 * @param {Object} data - Firestore 문서 데이터
 * @returns {number} 문서 크기 (bytes)
 */
export const calculateDocumentSize = (data) => {
  const jsonString = JSON.stringify(data);
  return new Blob([jsonString]).size;
};

/**
 * 문서 크기 상세 정보 반환
 * @param {string} userID - 사용자 ID
 * @param {string} docId - 문서 ID (기본값: "threads")
 * @returns {Promise<Object|null>} 문서 크기 정보
 */
export const getDocumentSize = async (userID, docId = "threads") => {
  try {
    const threadsDoc = await db.collection("user").doc(userID)
      .collection("threads").doc(docId).get();
    
    if (threadsDoc.exists) {
      const data = threadsDoc.data();
      const sizeInBytes = calculateDocumentSize(data);
      
      return {
        bytes: sizeInBytes,
        mb: (sizeInBytes / 1024 / 1024).toFixed(2),
        threadsCount: data.list?.length || 0,
        isNearLimit: sizeInBytes > 950 * 1024, // 950KB 임계점
        percentUsed: ((sizeInBytes / (1024 * 1024)) * 100).toFixed(1)
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting document size:', error);
    return null;
  }
};

/**
 * 메타데이터 초기화
 * @param {string} userID - 사용자 ID
 */
export const initializeShardingMetadata = async (userID) => {
  try {
    const userRef = db.collection("user").doc(userID);
    const threadsDoc = await userRef.collection("threads").doc("threads").get();
    
    if (threadsDoc.exists) {
      const size = calculateDocumentSize(threadsDoc.data());
      const isApproachingLimit = size > 950 * 1024;
      
      await userRef.update({
        threadsMetadata: {
          nextTargetDocument: isApproachingLimit ? "threads-1" : "threads",
          currentDocumentSize: size,
          isApproachingLimit: isApproachingLimit,
          totalDocuments: 1,
          documentSizes: {
            "threads": {
              size: size,
              count: threadsDoc.data().list?.length || 0
            }
          },
          lastUpdatedAt: new Date().toISOString()
        }
      });
      
      console.log(`Initialized sharding metadata for user ${userID}:`, {
        size: size,
        isApproachingLimit: isApproachingLimit,
        nextTarget: isApproachingLimit ? "threads-1" : "threads"
      });
    }
  } catch (error) {
    console.error('Error initializing sharding metadata:', error);
    throw error;
  }
};

/**
 * 타겟 문서 결정
 * @param {string} userID - 사용자 ID
 * @returns {Promise<string>} 타겟 문서 ID
 */
export const determineTargetDocument = async (userID) => {
  try {
    const userDoc = await db.collection("user").doc(userID).get();
    const metadata = userDoc.data()?.threadsMetadata;
    
    if (metadata?.nextTargetDocument) {
      return metadata.nextTargetDocument;
    }
    
    // 메타데이터가 없으면 초기화
    await initializeShardingMetadata(userID);
    return "threads";
  } catch (error) {
    console.error('Error determining target document:', error);
    return "threads"; // 기본값으로 fallback
  }
};

/**
 * 메타데이터 업데이트
 * @param {string} userID - 사용자 ID
 * @param {Object} updates - 업데이트할 메타데이터
 */
export const updateThreadsMetadata = async (userID, updates) => {
  try {
    const userRef = db.collection("user").doc(userID);
    
    await userRef.update({
      threadsMetadata: {
        ...updates,
        lastUpdatedAt: new Date().toISOString()
      }
    });
    
    console.log(`Updated threads metadata for user ${userID}:`, updates);
  } catch (error) {
    console.error('Error updating threads metadata:', error);
    throw error;
  }
};

/**
 * 다음 문서 번호 계산
 * @param {string} currentDoc - 현재 문서 ID
 * @returns {string} 다음 문서 ID
 */
export const getNextDocumentNumber = (currentDoc) => {
  if (currentDoc === "threads") {
    return "threads-1";
  }
  
  const currentNumber = parseInt(currentDoc.split("-")[1]);
  return `threads-${currentNumber + 1}`;
};

/**
 * 문서 배열 생성 (메타데이터 기반)
 * @param {number} totalDocuments - 총 문서 수
 * @returns {Array<string>} 문서 ID 배열
 */
export const generateDocumentList = (totalDocuments) => {
  if (totalDocuments <= 1) {
    return ["threads"];
  }
  
  const docs = ["threads"];
  for (let i = 1; i < totalDocuments; i++) {
    docs.push(`threads-${i}`);
  }
  
  return docs;
};

/**
 * 950KB 임계점 도달 여부 확인 및 처리
 * @param {string} userID - 사용자 ID
 * @param {string} targetDoc - 현재 타겟 문서
 * @returns {Promise<Object>} 처리 결과
 */
export const checkAndHandleThreshold = async (userID, targetDoc) => {
  try {
    const sizeInfo = await getDocumentSize(userID, targetDoc);
    
    if (!sizeInfo) {
      return { needsNewDocument: false, currentSize: 0 };
    }
    
    if (sizeInfo.isNearLimit) {
      const nextDoc = getNextDocumentNumber(targetDoc);
      
      // 메타데이터 업데이트
      await updateThreadsMetadata(userID, {
        nextTargetDocument: nextDoc,
        isApproachingLimit: true,
        currentDocumentSize: sizeInfo.bytes,
        [`documentSizes.${targetDoc}`]: {
          size: sizeInfo.bytes,
          count: sizeInfo.threadsCount
        }
      });
      
      return {
        needsNewDocument: true,
        currentSize: sizeInfo.bytes,
        nextDocument: nextDoc,
        currentDocument: targetDoc
      };
    }
    
    return {
      needsNewDocument: false,
      currentSize: sizeInfo.bytes,
      currentDocument: targetDoc
    };
  } catch (error) {
    console.error('Error checking threshold:', error);
    return { needsNewDocument: false, currentSize: 0 };
  }
};